import { useMutation, useQuery } from '@tanstack/react-query';
import { AlertTriangle, CheckCircle2, Flame, ShieldCheck, XCircle } from 'lucide-react';
import { smtpApi, warmupStatusLine } from '../../api/smtp';
import type { SMTPPreflightResult } from '../../api/smtp';
import type { SMTP } from '../../types';
import { Button } from '../../components/ui/Button';
import { Modal } from '../../components/ui/Modal';

interface SMTPHealthModalProps {
  isOpen: boolean;
  onClose: () => void;
  smtp: SMTP | null;
}

function Stat({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="bg-gray-50 rounded-lg p-3">
      <p className="text-xs text-gray-500">{label}</p>
      <p className="text-lg font-semibold text-gray-900">{value}</p>
    </div>
  );
}

function CheckLine({ ok, label, detail }: { ok: boolean; label: string; detail?: string }) {
  const Icon = ok ? CheckCircle2 : AlertTriangle;
  return (
    <li className="flex items-start gap-2">
      <Icon className={`w-4 h-4 mt-0.5 shrink-0 ${ok ? 'text-green-600' : 'text-amber-500'}`} />
      <div className="min-w-0">
        <p className="text-sm font-medium text-gray-900">{label}</p>
        {detail && <p className="text-xs text-gray-500 break-all">{detail}</p>}
      </div>
    </li>
  );
}

function PreflightResults({ result }: { result: SMTPPreflightResult }) {
  return (
    <div className="space-y-4">
      <ul className="space-y-2">
        <CheckLine
          ok={result.smtp_ok}
          label={result.smtp_ok ? 'SMTP connection and authentication succeeded' : 'SMTP connection failed'}
          detail={result.smtp_error}
        />
        <CheckLine
          ok={result.spf.found}
          label={result.spf.found ? 'SPF record found' : 'No SPF record'}
          detail={result.spf.record ?? result.spf.error}
        />
        <CheckLine
          ok={result.dmarc.found}
          label={result.dmarc.found ? 'DMARC record found' : 'No DMARC record'}
          detail={result.dmarc.record ?? result.dmarc.error}
        />
        <CheckLine
          ok={result.mx.found}
          label={result.mx.found ? 'MX records found' : 'No MX records'}
          detail={result.mx.records?.join(', ') ?? result.mx.error}
        />
        {result.dkim?.selector ? (
          <CheckLine
            ok={result.dkim.found && result.dkim.has_public_key}
            label={
              result.dkim.found
                ? `DKIM record found for selector ${result.dkim.selector}`
                : `No DKIM record for selector ${result.dkim.selector}`
            }
            detail={result.dkim.record ?? result.dkim.error}
          />
        ) : (
          <CheckLine
            ok={false}
            label="DKIM selector not configured"
            detail="Set a DKIM selector on the sending profile to verify signing"
          />
        )}
        {result.ptr && (
          <CheckLine
            ok={result.ptr.found && result.ptr.forward_confirmed}
            label={
              !result.ptr.host
                ? 'PTR check skipped'
                : result.ptr.found && result.ptr.forward_confirmed
                  ? 'PTR/FCrDNS verified for SMTP host'
                  : result.ptr.found
                    ? 'PTR exists but does not map back to the SMTP host'
                    : 'No PTR record for SMTP host'
            }
            detail={
              result.ptr.ptr_names?.join(', ') ??
              result.ptr.error ??
              result.ptr.addresses?.join(', ')
            }
          />
        )}
      </ul>
      {result.warnings.length > 0 && (
        <ul className="space-y-1 rounded-lg bg-amber-50 p-3">
          {result.warnings.map((warning) => (
            <li key={warning} className="flex items-start gap-2 text-xs text-amber-800">
              <AlertTriangle className="w-3 h-3 mt-0.5 shrink-0" />
              {warning}
            </li>
          ))}
        </ul>
      )}
      {result.note && <p className="text-xs text-gray-400">{result.note}</p>}
    </div>
  );
}

export function SMTPHealthModal({ isOpen, onClose, smtp }: SMTPHealthModalProps) {
  const { data: health, isLoading, isError } = useQuery({
    queryKey: ['smtp-health', smtp?.id],
    enabled: isOpen && smtp != null,
    queryFn: async () => {
      const res = await smtpApi.getHealth(smtp!.id);
      return res.data;
    },
  });

  const preflight = useMutation({
    mutationFn: () => smtpApi.runPreflight(smtp!.id),
  });

  const successRate = health ? `${(health.success_rate * 100).toFixed(1)}%` : '—';
  const warmupLine = health ? warmupStatusLine(health) : null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={`Delivery Health — ${smtp?.name ?? ''}`} size="lg">
      {isLoading && <p className="text-sm text-gray-500">Loading health…</p>}
      {isError && <p className="text-sm text-red-600">Could not load delivery health.</p>}
      {health && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <Stat label="Success rate" value={successRate} />
            <Stat label="Attempted" value={health.attempted} />
            <Stat label="Succeeded" value={health.succeeded} />
            <Stat label="Failed" value={health.failed} />
            <Stat label="Retrying" value={health.retrying} />
            <Stat label="Queued" value={health.queued} />
            <Stat label="In backoff" value={health.backoff} />
          </div>
          {warmupLine && (
            <div className="rounded-lg bg-orange-50 p-3 flex items-start gap-2">
              <Flame className="w-4 h-4 mt-0.5 shrink-0 text-orange-500" />
              <div>
                <p className="text-xs font-medium text-orange-800">Warmup mode</p>
                <p className="text-xs text-orange-700">{warmupLine}</p>
                {health.warmup_target_daily ? (
                  <p className="text-xs text-orange-500">
                    Target {health.warmup_target_daily}/day
                  </p>
                ) : null}
              </div>
            </div>
          )}
          {health.last_error && (
            <div className="rounded-lg bg-red-50 p-3">
              <p className="text-xs font-medium text-red-800">Last error</p>
              <p className="text-xs text-red-700 break-all">{health.last_error}</p>
              {health.last_error_time && (
                <p className="text-xs text-red-400">{new Date(health.last_error_time).toLocaleString()}</p>
              )}
            </div>
          )}
          {health.note && <p className="text-xs text-gray-400">{health.note}</p>}
        </div>
      )}

      <div className="mt-6 border-t pt-4 space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <ShieldCheck className="w-4 h-4 text-indigo-600" />
            <h2 className="text-sm font-semibold text-gray-900">Deliverability preflight</h2>
          </div>
          <Button
            size="sm"
            variant="secondary"
            loading={preflight.isPending}
            onClick={() => preflight.mutate()}
            disabled={smtp == null}
          >
            Run preflight
          </Button>
        </div>
        {preflight.isError && (
          <p className="text-sm text-red-600 flex items-center gap-2">
            <XCircle className="w-4 h-4" /> Preflight check failed to run.
          </p>
        )}
        {preflight.data && <PreflightResults result={preflight.data.data} />}
      </div>
    </Modal>
  );
}
